// pages/PasswordResetRequest.js - v3.0 Password Reset Request Page

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import publicApiService from '../services/api/publicApi';

const PasswordResetRequest = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError(null);

      // Use publicApiService for password reset (no auth needed)
      await publicApiService.requestPasswordReset(email);

      setSuccess(true);
    } catch (err) {
      console.error('Password reset request error:', err);
      setError(err.response?.data?.error || 'فشل إرسال طلب إعادة تعيين كلمة المرور');
    } finally {
      setLoading(false);
    }
  };

  const styles = {
    container: {
      fontFamily: "'Cairo', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: 'radial-gradient(circle at 15% 20%, rgba(255, 204, 0, 0.08), transparent 30%), radial-gradient(circle at 85% 80%, rgba(107, 70, 193, 0.16), transparent 35%), linear-gradient(135deg, #0B0B1A 0%, #0F0F23 55%, #15152A 100%)',
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
      direction: 'rtl',
    },
    card: {
      background: '#FFFFFF',
      borderRadius: '18px',
      padding: '40px',
      width: '100%',
      maxWidth: '500px',
      boxShadow: '0 10px 30px rgba(107, 70, 193, 0.08)',
      border: '1px solid #E5E7EB',
    },
    logoContainer: {
      textAlign: 'center',
      marginBottom: '32px',
    },
    logo: {
      width: '160px',
      height: 'auto',
      marginBottom: '24px',
    },
    title: {
      fontSize: '24px',
      color: '#1F2937',
      marginBottom: '8px',
      fontWeight: 700,
      textAlign: 'center',
    },
    subtitle: {
      color: '#6B7280',
      fontSize: '15px',
      marginBottom: '24px',
      textAlign: 'center',
      lineHeight: 1.6,
    },
    alert: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '12px 14px',
      borderRadius: '12px',
      fontSize: '14px',
      marginBottom: '20px',
    },
    alertError: {
      color: '#B91C1C',
      background: 'rgba(185, 28, 28, 0.08)',
      border: '1px solid rgba(185, 28, 28, 0.2)',
    },
    alertSuccess: {
      color: '#0F9D58',
      background: 'rgba(15, 157, 88, 0.08)',
      border: '1px solid rgba(15, 157, 88, 0.2)',
    },
    field: {
      marginBottom: '20px',
    },
    label: {
      display: 'block',
      fontSize: '14px',
      fontWeight: 600,
      color: '#1F2937',
      marginBottom: '8px',
      textAlign: 'right',
    },
    inputShell: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
    },
    input: {
      width: '100%',
      padding: '14px 44px 14px 14px',
      borderRadius: '14px',
      border: '1.5px solid #E5E7EB',
      background: '#FAFBFF',
      fontSize: '15px',
      fontFamily: 'inherit',
      transition: 'border-color 0.22s ease, box-shadow 0.22s ease, background 0.22s ease',
      outline: 'none',
      direction: 'rtl',
      textAlign: 'right',
    },
    button: {
      width: '100%',
      padding: '14px',
      border: 'none',
      borderRadius: '14px',
      fontSize: '16px',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'transform 0.22s ease, box-shadow 0.22s ease, opacity 0.22s ease',
      background: 'linear-gradient(135deg, #6B46C1 0%, #55309E 100%)',
      color: '#FFFFFF',
      boxShadow: '0 12px 30px rgba(107, 70, 193, 0.25)',
    },
    buttonDisabled: {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
    backLink: {
      textAlign: 'center',
      marginTop: '24px',
      fontSize: '14px',
    },
    link: {
      color: '#6B46C1',
      fontWeight: 600,
      textDecoration: 'none',
      transition: 'color 0.22s ease',
    },
  };

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logoContainer}>
          <img src="/images/Host Me logo 2.png" alt="Host Me" style={styles.logo} />
        </div>

        <h2 style={styles.title}>نسيت كلمة المرور؟</h2>
        <p style={styles.subtitle}>أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة تعيين كلمة المرور</p>

        {error && (
          <div style={{...styles.alert, ...styles.alertError}}>
            <span aria-hidden="true">!</span>
            <span>{error}</span>
          </div>
        )}

        {success ? (
          <div style={{...styles.alert, ...styles.alertSuccess}}>
            <span aria-hidden="true">✓</span>
            <span>تم إرسال رابط إعادة تعيين كلمة المرور إلى بريدك الإلكتروني. يرجى التحقق من صندوق الوارد الخاص بك.</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={styles.field}>
              <label htmlFor="email" style={styles.label}>البريد الإلكتروني</label>
              <div style={styles.inputShell}>
                <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false" style={{
                  position: 'absolute',
                  left: '14px',
                  width: '20px',
                  height: '20px',
                  color: '#6B7280',
                  pointerEvents: 'none',
                }}>
                  <path fill="currentColor" d="M20 4H4a2 2 0 0 0-2 2v12c0 1.1.9 2 2 2h16a2 2 0 0 0 2-2V6c0-1.1-.9-2-2-2Zm0 2v.01L12 12 4 6.01V6h16ZM4 18V8l8 6 8-6v10H4Z"/>
                </svg>
                <input
                  type="email"
                  id="email"
                  name="email"
                  placeholder="أدخل بريدك الإلكتروني"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={styles.input}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#6B46C1';
                    e.target.style.background = '#FFFFFF';
                    e.target.style.boxShadow = '0 0 0 4px rgba(107, 70, 193, 0.12)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#E5E7EB';
                    e.target.style.background = '#FAFBFF';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{...styles.button, ...(loading ? styles.buttonDisabled : {})}}
              onMouseOver={(e) => {
                if (!loading) {
                  e.target.style.transform = 'translateY(-1px)';
                  e.target.style.boxShadow = '0 14px 36px rgba(107, 70, 193, 0.32)';
                }
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 12px 30px rgba(107, 70, 193, 0.25)';
              }}
            >
              {loading ? 'جاري الإرسال...' : 'إرسال رابط إعادة التعيين'}
            </button>
          </form>
        )}

        <div style={styles.backLink}>
          <Link
            to="/login"
            style={styles.link}
            onMouseOver={(e) => e.target.style.color = '#55309E'}
            onMouseOut={(e) => e.target.style.color = '#6B46C1'}
          >
            العودة لتسجيل الدخول
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PasswordResetRequest;
